import { useChat } from "@ai-sdk/react";
import {
    ArrowUpOutlined,
    CheckCircleOutlined,
    ClearOutlined,
    CloseOutlined,
    FileTextOutlined,
    HistoryOutlined,
    LoadingOutlined,
    PaperClipOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    ReloadOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from "ai";
import { fetchAuthSession } from "aws-amplify/auth";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useHistory, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { getCommentsPermissionRequest } from "store/comments/actions";
import { clearAiHighlightedDataObjects } from "store/dataObjects/actions";
import { getDataSetFieldInsightsRequest } from "store/dataSets/actions";
import { getUsersRequest } from "store/users/actions";
import { createConversation, deleteConversation, getConversationMessages, listConversations, saveConversationMessages, type ConversationSummary } from "./conversationApi";
import ConversationHistory from "./ConversationHistory";
import { parseDataObjectsCsv, type ParsedDataObjectsCsv } from "./csv";
import { extractComments, extractDataSetContext, extractProfilingStats, summariseSchemaFields, trimDataObjectsForContext } from "./helpers";
import { tryParseStreamError } from "./streamError";
import { KNOWN_TOOL_PART_TYPES, renderToolPart } from "./tools";
import type { AttachedCsvRef, DataObjectSummary, DataSetSummary, SchemaFieldSummary, ToolHelpers } from "./tools/types";

interface DataSetAssistantContentProps {
    /** When omitted, the assistant computes permission from redux + userBusinessArea. */
    permission?: { canAdd?: boolean; canEdit?: boolean };
    /** When omitted, the assistant reads the dataSetAction param from the URL. */
    action?: "view" | "edit" | "create";
}

const COPILOT_API_BASE = process.env.REACT_APP_COPILOT_API || "https://24m7bef4jqvsp7pxnp5orbgm540npqzi.lambda-url.us-east-2.on.aws";
const ASSISTANT_DATASETS_URL = `${COPILOT_API_BASE.replace(/\/+$/, "")}/assistant/datasets`;

// The deployed copilot Lambda requires a Cognito JWT in the Authorization
// header, same as the rest of the app's API calls. Resolved fresh per
// request so we don't ship an expired token.
async function authHeaders(): Promise<Record<string, string>> {
    try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

export default function DataSetAssistantContent({ permission, action }: DataSetAssistantContentProps) {
    const [input, setInputText] = useState("");
    const [bulkApplyNonce, setBulkApplyNonce] = useState(0);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [attachedCsv, setAttachedCsv] = useState<ParsedDataObjectsCsv | null>(null);
    const [csvBusy, setCsvBusy] = useState(false);
    const [csvError, setCsvError] = useState<string | null>(null);

    // Conversation persistence (conversation-service). conversationId identifies
    // the active session for the current dataset; hydratedForRef tracks which
    // dataset we've already loaded so we hydrate exactly once per dataset.
    const [conversationId, setConversationId] = useState<string | null>(null);
    const conversationIdRef = useRef<string | null>(null);
    const hydratedForRef = useRef<string | null>(null);
    useEffect(() => {
        conversationIdRef.current = conversationId;
    }, [conversationId]);

    // Conversation history browser state.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);

    const handleUploadStarted = useCallback(() => {
        // Once the bulk-upload job is in flight, drop the attached CSV from
        // the chat so subsequent turns don't re-ship the sample. The tool
        // card stays in the conversation history as a confirmation.
        setAttachedCsv(null);
        setCsvError(null);
    }, []);

    const dispatch = useAppDispatch();
    const history = useHistory();
    const urlParams = useParams<{ tenant?: string; businessarea?: string; schema?: string; dataSetId?: string; dataSetAction?: string }>();

    const tenantId: string | undefined = useAppSelector((s: any) => s.userLogin?.tenant?.tenantId);
    const userBusinessAreas = useAppSelector((s: any) => s.schemaModels?.userBusinessAreas);
    const dataSet = useAppSelector((s: any) => s.dataSets?.dataSet);
    const schemaModel = useAppSelector((s: any) => s.schemaModels?.schemaModel);
    const dataObjectsItems: DataObjectSummary[] = useAppSelector((s: any) => s.dataObjects?.dataObjectsByDataSetIdResult?.Items || []);
    const updateDataSetResult = useAppSelector((s: any) => s.dataSets?.updateDataSetResult);
    const createDataObjectResult = useAppSelector((s: any) => s.dataObjects?.createDataObjectResult);
    const updateDataObjectResult = useAppSelector((s: any) => s.dataObjects?.updateDataObjectResult);
    const bulkUploadResult = useAppSelector((s: any) => s.dataObjects?.createDataObjectsUploadJobResult);
    const dataSetReduxPermission = useAppSelector((s: any) => s.dataSets?.permission);
    // NEW: Fetch field insights (profiling stats) and comments
    const fieldInsightsResult = useAppSelector((s: any) => s.dataSets?.dataSetFieldInsightsResult);
    const commentsByRecordId = useAppSelector((s: any) => s.comments?.commentsByRecordId);
    // NEW: comment add permission, add-comment result, and user list (for @mention resolution)
    const userCommentsPermission = useAppSelector((s: any) => s.comments?.userCommentsPermission);
    const addCommentResult = useAppSelector((s: any) => s.comments?.addCommentResult);
    const userList = useAppSelector((s: any) => s.users?.userList || []);

    const businessArea = useMemo(() => {
        return urlParams.businessarea ? userBusinessAreas?.[urlParams.businessarea.toLowerCase()] : undefined;
    }, [urlParams.businessarea, userBusinessAreas]);

    const businessAreaId = businessArea?.businessAreaId;
    const schemaId = dataSet?.schemaId || schemaModel?.schemaId;

    const effectivePermission = useMemo(() => {
        if (permission) {
            return { canAdd: !!permission.canAdd, canEdit: !!permission.canEdit };
        }
        const userActions = businessArea?.user;
        const baseCanAdd = !!dataSetReduxPermission?.canAdd && !!userActions?.canAdd;
        const baseCanEdit = !!dataSetReduxPermission?.canEdit && !!userActions?.canEdit;
        return { canAdd: baseCanAdd, canEdit: baseCanEdit };
    }, [permission, dataSetReduxPermission, businessArea]);

    const effectiveAction: "view" | "edit" | "create" | undefined = action || (urlParams.dataSetAction as any);

    const dataSetSummary: DataSetSummary | undefined = useMemo(() => {
        if (!dataSet) return undefined;
        return {
            dataSetId: dataSet.dataSetId,
            dataSetName: dataSet.dataSetName,
            dataSetDescription: dataSet.dataSetDescription,
            schemaId: dataSet.schemaId,
            businessAreaId: dataSet.businessAreaId,
            version: dataSet.version,
            metadata: dataSet.metadata,
        };
    }, [dataSet]);

    const schemaFields: SchemaFieldSummary[] = useMemo(() => summariseSchemaFields(schemaModel?.fields), [schemaModel]);

    // NEW: Extract profiling stats and comments
    const profilingStats = useMemo(() => extractProfilingStats(fieldInsightsResult?.Items), [fieldInsightsResult]);
    const comments = useMemo(() => {
        if (!dataSet?.dataSetId || !commentsByRecordId) return [];
        return extractComments(commentsByRecordId[dataSet.dataSetId]);
    }, [dataSet?.dataSetId, commentsByRecordId]);

    const commentPermission = useMemo(
        () => ({
            canAdd: !!userCommentsPermission?.canAdd,
            canView: !!userCommentsPermission?.canView,
        }),
        [userCommentsPermission],
    );

    useEffect(() => {
        if (!userCommentsPermission) dispatch(getCommentsPermissionRequest());
    }, [dispatch, userCommentsPermission]);
    useEffect(() => {
        if (!userList || userList.length === 0) dispatch(getUsersRequest());
    }, [dispatch, userList]);
    useEffect(() => {
        const dataSetId = dataSet?.dataSetId;
        if (!dataSetId || !tenantId) return;
        dispatch(getDataSetFieldInsightsRequest(dataSetId));
    }, [dispatch, dataSet?.dataSetId, tenantId]);

    // Capture live context into refs so prepareSendMessagesRequest reads current values
    const ctxRef = useRef({
        dataSet: undefined as any,
        dataObjectsItems: [] as DataObjectSummary[],
        schemaModelFields: undefined as any,
        schemaFields: [] as SchemaFieldSummary[],
        effectiveAction: undefined as "view" | "edit" | "create" | undefined,
        effectivePermission: { canAdd: false, canEdit: false },
        attachedCsv: null as ParsedDataObjectsCsv | null,
        profilingStats: [] as any[],
        comments: [] as any[],
        commentPermission: { canAdd: false, canView: false },
    });
    useEffect(() => {
        ctxRef.current = {
            dataSet,
            dataObjectsItems,
            schemaModelFields: schemaModel?.fields,
            schemaFields,
            effectiveAction,
            effectivePermission,
            attachedCsv,
            profilingStats,
            comments,
            commentPermission,
        };
    }, [dataSet, dataObjectsItems, schemaModel, schemaFields, effectiveAction, effectivePermission, attachedCsv, profilingStats, comments, commentPermission]);

    const transport = useMemo(
        () =>
            new DefaultChatTransport({
                api: ASSISTANT_DATASETS_URL,
                headers: authHeaders,
                prepareSendMessagesRequest: ({ messages, body }) => {
                    const ctx = ctxRef.current;
                    const trimmedDataObjects = trimDataObjectsForContext(ctx.dataObjectsItems, ctx.schemaModelFields);
                    const payload: any = {
                        ...(body || {}),
                        messages,
                        dataSet: extractDataSetContext(ctx.dataSet),
                        dataObjects: trimmedDataObjects,
                        mode: {
                            action: ctx.effectiveAction,
                            permission: ctx.effectivePermission,
                            commentPermission: ctx.commentPermission,
                            schemaFields: ctx.schemaFields,
                        },
                    };

                    // Optional CSV attachment
                    if (ctx.attachedCsv) {
                        payload.csv = {
                            fileName: ctx.attachedCsv.fileName,
                            rowCount: ctx.attachedCsv.rowCount,
                            columnCount: ctx.attachedCsv.columnCount,
                            sampledRowCount: ctx.attachedCsv.sampledRowCount,
                            sampledColumnCount: ctx.attachedCsv.sampledColumnCount,
                            truncated: ctx.attachedCsv.truncated,
                            sampleRows: ctx.attachedCsv.sampleRows,
                        };
                    }

                    // Optional profiling stats and comments
                    if (ctx.profilingStats.length > 0) {
                        payload.profilingStats = ctx.profilingStats;
                    }
                    if (ctx.comments.length > 0) {
                        payload.comments = ctx.comments;
                    }

                    return { body: payload };
                },
            }),
        [],
    );

    const { messages, sendMessage, status, stop, error, clearError, regenerate, addToolOutput, setMessages } = useChat({
        transport,
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
        onFinish: () => {
            const convId = conversationIdRef.current;
            const dsId = dataSet?.dataSetId;
            if (!convId || !dsId || !tenantId) return;
            setMessages((current) => {
                void saveConversationMessages(dsId, convId, current as any).catch((e) => {
                    // Persistence is best-effort; never break the chat on a save failure.
                    console.warn("Failed to persist conversation", e);
                });
                return current;
            });
        },
    });

    useEffect(() => {
        const dsId = dataSet?.dataSetId;
        if (!dsId || !tenantId) return;
        if (hydratedForRef.current === dsId) return;
        hydratedForRef.current = dsId;
        let cancelled = false;
        (async () => {
            try {
                const conversations = await listConversations(dsId);
                let convId: string;
                if (conversations.length > 0) {
                    convId = conversations[0].conversationId;
                    const stored = await getConversationMessages(convId);
                    if (!cancelled && Array.isArray(stored) && stored.length > 0) {
                        setMessages(stored as any);
                    }
                } else {
                    const created = await createConversation(dsId);
                    convId = created.conversationId;
                }
                if (!cancelled) setConversationId(convId);
            } catch (e) {
                // If history can't be loaded the chat still works in-memory; just
                // reset the guard so a later dataset change can retry.
                console.warn("Failed to load conversation history", e);
                if (!cancelled && hydratedForRef.current === dsId) hydratedForRef.current = null;
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dataSet?.dataSetId, tenantId, setMessages]);

    const handleClearChat = useCallback(() => {
        setMessages([]);
        setAttachedCsv(null);
        setCsvError(null);
        dispatch(clearAiHighlightedDataObjects());
        const dsId = dataSet?.dataSetId;
        const convId = conversationIdRef.current;
        if (dsId && tenantId && convId) {
            void deleteConversation(dsId, convId).catch((e) => console.warn("Failed to delete conversation", e));
        }
        // Open a fresh conversation for future turns.
        if (dsId && tenantId) {
            setConversationId(null);
            void createConversation(dsId)
                .then((c) => setConversationId(c.conversationId))
                .catch((e) => console.warn("Failed to create conversation", e));
        }
    }, [setMessages, dispatch, dataSet?.dataSetId, tenantId]);

    // Start a new session: keep the previous conversation persisted server-side
    // (do NOT delete it), just clear the view and rotate to a fresh conversation.
    const handleNewChat = useCallback(() => {
        setMessages([]);
        setAttachedCsv(null);
        setCsvError(null);
        dispatch(clearAiHighlightedDataObjects());
        const dsId = dataSet?.dataSetId;
        if (dsId && tenantId) {
            setConversationId(null);
            void createConversation(dsId)
                .then((c) => setConversationId(c.conversationId))
                .catch((e) => console.warn("Failed to create conversation", e));
        }
    }, [setMessages, dispatch, dataSet?.dataSetId, tenantId]);

    // Open the history browser and (re)load the list of conversations for this
    // dataset. Persists the current thread first so its latest state shows up.
    const handleOpenHistory = useCallback(async () => {
        const dsId = dataSet?.dataSetId;
        if (!dsId || !tenantId) return;
        setHistoryOpen(true);
        setHistoryLoading(true);
        try {
            const list = await listConversations(dsId);
            setConversations(list);
        } catch (e) {
            console.warn("Failed to load conversation list", e);
            setConversations([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [dataSet?.dataSetId, tenantId]);

    // Load a past conversation into the chat and make it the active session, so
    // the user can read it and continue from where it left off.
    const handleSelectConversation = useCallback(
        async (selectedId: string) => {
            dispatch(clearAiHighlightedDataObjects());
            setAttachedCsv(null);
            setCsvError(null);
            try {
                const stored = await getConversationMessages(selectedId);
                setMessages((Array.isArray(stored) ? stored : []) as any);
                setConversationId(selectedId);
                setHistoryOpen(false);
            } catch (e) {
                console.warn("Failed to load conversation", e);
            }
        },
        [dispatch, setMessages],
    );

    // Delete a conversation from history. If it's the one currently open, also
    // clear the view and rotate to a fresh conversation so the user isn't left
    // editing a deleted thread.
    const handleDeleteConversation = useCallback(
        async (targetId: string) => {
            const dsId = dataSet?.dataSetId;
            if (!dsId) return;
            try {
                await deleteConversation(dsId, targetId);
            } catch (e) {
                console.warn("Failed to delete conversation", e);
                return;
            }
            setConversations((prev) => prev.filter((c) => c.conversationId !== targetId));
            if (conversationIdRef.current === targetId) {
                setMessages([]);
                dispatch(clearAiHighlightedDataObjects());
                setConversationId(null);
                if (tenantId) {
                    void createConversation(dsId)
                        .then((c) => setConversationId(c.conversationId))
                        .catch((e) => console.warn("Failed to create conversation", e));
                }
            }
        },
        [dataSet?.dataSetId, tenantId, setMessages, dispatch],
    );

    const attachedCsvRef: AttachedCsvRef | null = useMemo(() => {
        if (!attachedCsv) return null;
        return {
            file: attachedCsv.file,
            fileName: attachedCsv.fileName,
            rowCount: attachedCsv.rowCount,
            columnCount: attachedCsv.columnCount,
        };
    }, [attachedCsv]);

    const helpers: ToolHelpers = useMemo(
        () => ({
            addToolOutput: (args) => {
                addToolOutput(args as any);
            },
            dispatch,
            history,
            urlParams,
            tenantId,
            businessAreaId,
            schemaId,
            dataSet: dataSetSummary,
            dataObjects: dataObjectsItems,
            schemaFields,
            permission: effectivePermission,
            mode: effectiveAction,
            updateDataSetResult,
            createDataObjectResult,
            updateDataObjectResult,
            addCommentResult,
            commentPermission,
            users: userList,
            attachedCsv: attachedCsvRef,
            bulkUploadResult,
            onUploadStarted: handleUploadStarted,
            bulkApplyNonce,
        }),
        [
            addToolOutput,
            dispatch,
            history,
            urlParams,
            tenantId,
            businessAreaId,
            schemaId,
            dataSetSummary,
            dataObjectsItems,
            schemaFields,
            effectivePermission,
            effectiveAction,
            updateDataSetResult,
            createDataObjectResult,
            updateDataObjectResult,
            addCommentResult,
            commentPermission,
            userList,
            attachedCsvRef,
            bulkUploadResult,
            handleUploadStarted,
            bulkApplyNonce,
        ],
    );

    const isBusy = status === "submitted" || status === "streaming";

    const pendingUpdateToolCount = useMemo(() => {
        const pendingIds = new Set<string>();
        (messages || []).forEach((m: any) => {
            const parts = Array.isArray(m?.parts) ? m.parts : [];
            parts.forEach((part: any) => {
                if (part?.type === "tool-updateDataObject" && part?.state === "input-available" && part?.toolCallId) {
                    pendingIds.add(part.toolCallId);
                }
            });
        });
        return pendingIds.size;
    }, [messages]);

    const showBulkApplyBar = pendingUpdateToolCount > 1;

    const handleBulkApply = useCallback(() => {
        if (pendingUpdateToolCount < 2 || isBusy) return;
        setBulkApplyNonce((prev) => prev + 1);
    }, [pendingUpdateToolCount, isBusy]);

    const handleBulkCancel = useCallback(() => {
        if (pendingUpdateToolCount < 2 || isBusy) return;
        // Find all pending update tool calls and cancel them
        (messages || []).forEach((m: any) => {
            const parts = Array.isArray(m?.parts) ? m.parts : [];
            parts.forEach((part: any) => {
                if (part?.type === "tool-updateDataObject" && part?.state === "input-available" && part?.toolCallId) {
                    addToolOutput({ tool: "updateDataObject", toolCallId: part.toolCallId, output: { success: false, cancelled: true } });
                }
            });
        });
    }, [pendingUpdateToolCount, isBusy, messages, addToolOutput]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, status]);

    // Clear AI highlights on unmount.
    useEffect(() => {
        return () => {
            dispatch(clearAiHighlightedDataObjects());
        };
    }, [dispatch]);

    const handleSend = useCallback(() => {
        const text = input.trim();
        if (!text || isBusy) return;
        // Clear any lingering error banner from a previous failed turn so the
        // retry starts clean (rate-limit / network errors leave status="error").
        clearError?.();
        // New user message clears prior highlights so each turn starts fresh.
        dispatch(clearAiHighlightedDataObjects());
        sendMessage({ text });
        setInputText("");
    }, [input, isBusy, dispatch, sendMessage, clearError]);

    // One-click retry for a failed turn (e.g. a 429 rate-limit that survived the
    // backend's auto-retries). Clears the stale error banner and re-runs the last
    // user turn via useChat's regenerate() — the user does not have to retype.
    const handleRetry = useCallback(() => {
        if (isBusy) return;
        clearError?.();
        void regenerate();
    }, [isBusy, clearError, regenerate]);
    const handlePickFile = () => {
        if (csvBusy || isBusy) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file) return;
        if (!/\.csv$/i.test(file.name)) {
            setCsvError("Only .csv files are supported.");
            return;
        }
        setCsvBusy(true);
        setCsvError(null);
        try {
            const parsed = await parseDataObjectsCsv(file);
            if (parsed.rowCount === 0 && parsed.columnCount === 0) {
                setCsvError("That file looks empty.");
                setAttachedCsv(null);
            } else {
                setAttachedCsv(parsed);
            }
        } catch (err: any) {
            setCsvError(err?.message || "Couldn't parse that CSV.");
            setAttachedCsv(null);
        } finally {
            setCsvBusy(false);
        }
    };

    const handleClearCsv = () => {
        setAttachedCsv(null);
        setCsvError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="relative flex h-full min-h-0 flex-col bg-[#fafafa] text-zinc-900">
            {historyOpen && (
                <ConversationHistory
                    conversations={conversations}
                    loading={historyLoading}
                    activeConversationId={conversationId}
                    onSelect={handleSelectConversation}
                    onDelete={handleDeleteConversation}
                    onClose={() => setHistoryOpen(false)}
                />
            )}
            <div className="flex flex-none items-center justify-between gap-2 px-4 py-3">
                <p className="m-0 text-xs text-zinc-500">Responses are generated using AI and may contain mistakes.</p>
                <div className="flex flex-none items-center gap-1">
                    <button
                        type="button"
                        onClick={handleOpenHistory}
                        disabled={isBusy}
                        title="Conversation history"
                        className="flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <HistoryOutlined /> History
                    </button>
                    {messages.length > 0 && (
                        <>
                            <button
                                type="button"
                                onClick={handleClearChat}
                                disabled={isBusy}
                                title="Clear chat"
                                className="flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ClearOutlined /> Clear
                            </button>
                            <button
                                type="button"
                                onClick={handleNewChat}
                                disabled={isBusy}
                                title="Start a new session"
                                className="flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <PlusOutlined /> New chat
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
                {messages.map((m) => {
                    if (m.role === "user") {
                        const text = Array.isArray(m.parts)
                            ? m.parts
                                  .filter((p: any) => p?.type === "text")
                                  .map((p: any) => p.text || "")
                                  .join("")
                            : "";
                        if (!text) return null;
                        return (
                            <div key={m.id} className="flex justify-end">
                                <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[#001D3D] px-3 py-2 text-sm text-white">{text}</div>
                            </div>
                        );
                    }
                    const parts = Array.isArray(m.parts) ? m.parts : [];
                    return (
                        <div key={m.id} className="flex flex-col gap-2">
                            {parts.map((part: any, idx: number) => {
                                if (part.type === "text") {
                                    if (!part.text) return null;
                                    // Defensive: if the AI SDK leaked a provider error frame as plain
                                    // text, render it as a friendly red banner instead of as a chat bubble.
                                    const parsedErr = tryParseStreamError(part.text);
                                    if (parsedErr) {
                                        return (
                                            <div key={idx} className="flex justify-start">
                                                <div className="flex max-w-[90%] flex-col gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                                    <span>{parsedErr.friendly}</span>
                                                    <button
                                                        type="button"
                                                        onClick={handleRetry}
                                                        disabled={isBusy}
                                                        className="flex items-center gap-1.5 self-start rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        <ReloadOutlined spin={isBusy} /> Try again
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={idx} className="flex justify-start">
                                            <div className="max-w-[90%] rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800">
                                                <div className="dataset-assistant-markdown">
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.text}</ReactMarkdown>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                if (KNOWN_TOOL_PART_TYPES.has(part.type)) {
                                    return (
                                        <div key={idx} className="flex justify-start">
                                            <div className="w-full max-w-[95%]">{renderToolPart(part, helpers)}</div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    );
                })}
                {status === "submitted" && (
                    <div className="flex justify-start">
                        <div className="rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-500">
                            <LoadingOutlined spin /> Thinking...
                        </div>
                    </div>
                )}
                {error && (
                    <div className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        <span>{error.message || "Something went wrong."}</span>
                        <button
                            type="button"
                            onClick={handleRetry}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 self-start rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ReloadOutlined spin={isBusy} /> Try again
                        </button>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="flex flex-none flex-col gap-2 border-t border-zinc-200 bg-[#fafafa] px-4 py-3">
                <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
                {showBulkApplyBar && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                <CheckCircleOutlined className="flex-shrink-0 text-lg text-blue-600" />
                                <span className="truncate text-sm font-medium text-blue-900">
                                    {pendingUpdateToolCount} update{pendingUpdateToolCount === 1 ? "" : "s"} pending
                                </span>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleBulkCancel}
                                    disabled={isBusy}
                                    className="whitespace-nowrap rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Cancel all
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBulkApply}
                                    disabled={isBusy}
                                    className="whitespace-nowrap rounded-md bg-[#001D3D] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Apply all
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {attachedCsv && (
                    <div className="rounded-md border border-zinc-200 bg-white p-2 text-xs text-zinc-700">
                        <div className="flex items-center gap-2">
                            <FileTextOutlined className="text-[#001D3D]" />
                            <span className="truncate font-medium text-zinc-900">{attachedCsv.fileName}</span>
                            <span className="text-zinc-500">
                                · {attachedCsv.rowCount.toLocaleString()} row{attachedCsv.rowCount === 1 ? "" : "s"} · {attachedCsv.columnCount} col
                                {attachedCsv.columnCount === 1 ? "" : "s"}
                            </span>
                            <div className="flex-1" />
                            <button
                                type="button"
                                onClick={handleClearCsv}
                                className="rounded-full p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                                title="Remove attachment"
                            >
                                <CloseOutlined />
                            </button>
                        </div>
                        {(attachedCsv.truncated.rows || attachedCsv.truncated.columns) && (
                            <div className="mt-1 text-[10px] text-zinc-500">
                                Sampling {attachedCsv.sampledRowCount} of {attachedCsv.rowCount.toLocaleString()} rows
                                {attachedCsv.truncated.columns && ` and ${attachedCsv.sampledColumnCount} of ${attachedCsv.columnCount} columns`}.
                            </div>
                        )}
                        {attachedCsv.parseErrors.length > 0 && <div className="mt-1 text-[10px] text-amber-700">{attachedCsv.parseErrors[0]}</div>}
                        {attachedCsv.sampleRows.length > 0 && (
                            <div className="mt-2 overflow-x-auto rounded-md border border-zinc-200">
                                <table className="min-w-full border-collapse text-left text-[10px]">
                                    <tbody>
                                        {attachedCsv.sampleRows.slice(0, 3).map((row, ri) => (
                                            <tr key={ri} className="border-t border-zinc-200 first:border-t-0">
                                                {row.slice(0, 6).map((v, ci) => (
                                                    <td key={ci} className="max-w-[140px] truncate px-2 py-1 text-zinc-800">
                                                        {v === null || v === undefined ? <span className="text-zinc-400">∅</span> : String(v)}
                                                    </td>
                                                ))}
                                                {row.length > 6 && <td className="px-2 py-1 text-zinc-400">…</td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                {csvError && !attachedCsv && <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{csvError}</div>}
                <div className="rounded-2xl border border-[#001D3D]/40 bg-white px-3 py-2 focus-within:border-[#001D3D]">
                    <textarea
                        value={input}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={attachedCsv ? "What should I do with this file?" : "Ask about this data set..."}
                        rows={2}
                        className="w-full resize-none border-0 bg-transparent text-sm text-zinc-900 placeholder-zinc-400 outline-none"
                    />
                    <div className="mt-1 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={handlePickFile}
                            disabled={csvBusy || isBusy}
                            className="rounded-full p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                            title={attachedCsv ? "Replace CSV" : "Attach a CSV"}
                        >
                            {csvBusy ? <LoadingOutlined spin /> : <PaperClipOutlined />}
                        </button>
                        {isBusy ? (
                            <button
                                type="button"
                                onClick={() => stop()}
                                className="flex h-7 w-7 items-center justify-center rounded-full bg-[#001D3D] text-white hover:bg-[#002D5C]"
                                title="Stop"
                            >
                                <StopOutlined />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={!input.trim()}
                                className={`flex h-7 w-7 items-center justify-center rounded-full ${
                                    input.trim() ? "bg-[#001D3D] text-white hover:bg-[#002D5C]" : "cursor-not-allowed bg-zinc-200 text-zinc-400"
                                }`}
                                title="Send"
                            >
                                <ArrowUpOutlined />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <QuestionCircleOutlined />
                    <a href="mailto:support@pretectum.com" className="text-zinc-600 hover:text-zinc-900">
                        Contact support
                    </a>
                </div>
            </div>
        </div>
    );
}
